import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type CustomFieldResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Deletes a custom field.
 *
 * @param customFieldId - An uuid containing the custom field. details to be deleted.
 * @returns A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/delete_custom_field_custom_field__custom_field_id__delete
 */
export const deleteCustomField = async (customFieldId: UUID) => {
  const response = await vestigasApi
    .delete(ENDPOINT.CUSTOM_FIELD.DELETE(customFieldId))
    .json<CustomFieldResponse>();

  return response;
};

/**
 * Custom hook for deleting a custom field using mutations.
 *
 * @returns An object containing the mutation function and other properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Custom-Field/operation/delete_custom_field_custom_field__custom_field_id__delete
 */
export const useMutationDeleteCustomField = (options?: UseMutationOptions) =>
  useMutation({
    mutationFn: async (customFieldId: UUID) => deleteCustomField(customFieldId),
    ...options,
  });
